import { Fragment, useEffect, useRef, useState, useMemo } from 'react';
import { FacebookIcon } from '@ui/icons/social/facebook';
import { InstagramIcon } from '@ui/icons/social/instagram';
import { YoutubeIcon } from '@ui/icons/social/youtube';
import { LandingNavbar } from '@app/landing/landing-navbar';
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { color, motion } from "framer-motion";
import { Sparkles } from 'lucide-react';
import { Shield, ShieldCheck, Share2, UserPlus, Upload, Globe2 } from "lucide-react";
import {  Lock, Database, FileCheck, Users, Eye,  Headphones, Zap , KeyRound, Clock , CloudUpload  } from 'lucide-react';
import { BillingCycleRadio } from '@common/billing/pricing-table/billing-cycle-radio';
import { PricingTable } from '@common/billing/pricing-table/pricing-table';
import {
  ContactPagePayload,
  useSubmitContactForm,
} from '@common/contact/use-submit-contact-form';
import { useForm } from 'react-hook-form';
import { Dialog } from '@ui/overlays/dialog/dialog';
import { DialogBody } from '@ui/overlays/dialog/dialog-body';
import { DialogHeader } from '@ui/overlays/dialog/dialog-header';
import { DialogTrigger } from '@ui/overlays/dialog/dialog-trigger';
import { FormTextField } from '@ui/forms/input-field/text-field/text-field';
import { useRecaptcha } from '@common/recaptcha/use-recaptcha';
import { Button } from '@ui/buttons/button';
import { Trans } from '@ui/i18n/trans';
import { Form } from '@ui/forms/form';
import { UpsellBillingCycle } from '@common/billing/pricing-table/find-best-price';
import { useProducts } from '@common/billing/pricing-table/use-products';
import { CheckCircleOutlineIcon } from '@ui/icons/material/CheckCircleOutline';
import { FullPageLoader } from '@ui/progress/full-page-loader';
import { ArrowLeftIcon } from '@ui/icons/material/ArrowLeftIcon';
import { ArrowRightIcon } from '@ui/icons/material/ArrowRightIcon';
import { useThemeSelector } from '@ui/themes/theme-selector-context';
import { Helmet } from "@common/seo/helmet";

export function ContentCreators() {
  const query = useProducts('pricingPage');
    const [selectedCycle, setSelectedCycle] =
      useState<UpsellBillingCycle>('month');

  const { selectThemeTemporarily } = useThemeSelector();
  useEffect(() => {
    selectThemeTemporarily('light');
    return () => selectThemeTemporarily(null);
  }, [selectThemeTemporarily]);

 const steps = [
    {
      icon: UserPlus,
      number: '01',
      title: 'Sign Up',
      description: 'Create your account in seconds with no credit card required',
    },
    {
      icon: Upload,
      number: '02',
      title: 'Upload Files',
      description: 'Upload files or sync folders instantly with secure, high-speed cloud storage',
    },
    {
      icon: Globe2,
      number: '03',
      title: 'Access Anywhere',
      description: 'Access your data from any device, anywhere in the world',
    },
  ];

const cards  : {
    title: string;
    desc: React.ReactNode;
    icon: React.ReactNode;
    color: string;
  }[] = [
  {
    icon: <CloudUpload  className="w-24 h-24 text-white" />,
    title: " Fast & Reliable Uploads",
    desc: <>
      Upload large videos, RAW images, and audio files instantly with minimal lag. {" "}
      <a
        href="/"
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#0072ff] font-bold hover:underline"
      >
        SpaceByte 
      </a>{" "}
      high-speed cloud servers keep your content accessible and help you meet deadlines.
    </>,
    color: 'from-[#1E3A8A] to-[#3B82F6]',
  },
  {
    icon: <Users  className="w-24 h-24 text-white" />,
    title: "Seamless Collaboration",
    desc: "Easily share files, manage permissions, and collaborate in real-time. With version control and project tracking, your creative workflow stays smooth for teams and clients.",
    color: 'from-[#3B82F6] to-[#60A5FA]',
  },
  {
    icon: <Database  className="w-24 h-24 text-white" />,
    title: "Massive Storage & Scalability",
    desc: "Enjoy unlimited or expandable storage for high-resolution videos, photos, and audio files. SpaceByte scales with your creative library, perfect for small projects or large-scale production.",
    color: 'from-[#06B6D4] to-[#3B82F6]',
  },
    {
    icon: <ShieldCheck  className="w-24 h-24 text-white" />,
    title: "Advanced Security",
    desc: "Your photos, videos, and projects stay secure with strong encryption and safe login protection, preventing unauthorized access and data loss.",
    color: 'from-[#1E3A8A] to-[#06B6D4]',
  },
  {
    icon: <Headphones  className="w-24 h-24 text-white" />,
    title: " 24/7 Dedicated Support",
    desc: "Our support team is available anytime to help you resolve issues quickly and keep your creative projects running smoothly.",
    color: 'from-[#3B82F6] to-[#1E3A8A]',
  },
];

const securityFeatures = [
    {
      icon: Share2,
      title: 'Secure file sharing',
      description: 'Send large videos, photos, and project files instantly with password-protected links.',
    },
    {
      icon: KeyRound,
      title: 'Smart access control',
      description: 'Decide who can view, comment, or edit. No more accidental changes.',
    },
    {
      icon: Clock,
      title: 'Time-based access',
      description: 'Set expiration dates for client deliveries or campaign files.',
    },
    {
      icon: Zap,
      title: 'Fast performance',
      description: 'Share heavy media files without slow uploads or broken links',
    },
  ];

  const services = [
    {
      title: "Digital Marketers ",
      desc: <>
      Keep campaign creatives, ad videos, landing page assets for {" "}
      <a
        href="/cloud-storage-for-digital-marketing"
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#0072ff] font-bold hover:underline"
      >
        digital marketing 
      </a>{" "}
      , and performance reports organized while working smoothly with teams and clients.
    </>,
    },
    {
      title: "Social Media Managers",
      desc: <>
      Manage brand content, reels, captions, visuals, and analytics as a {" "}
      <a
        href="/cloud-storage-for-social-media-managers"
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#0072ff] font-bold hover:underline"
      >
         social media manager 
      </a>{" "}
      across platforms without losing track of important files.
    </>,
    },
    {
      title: "SEO Specialists",
      desc: <>
      Store keyword research, website backups, analytics reports, and client documents for {" "}
      <a
        href="/cloud-storage-for-seo-specialists"
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#0072ff] font-bold hover:underline"
      >
         SEO specialists  
      </a>{" "}
      in a structured, easy-to-access workspace.
    </>,
    },
    {
      title: "Influencers & Brand Creators",
      desc: <>
      Share media kits, sponsored content, collaboration files, and high-resolution photos as influencers creators easily with {" "}
      <a
        href="/cloud-storage-for-brand-creators"
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#0072ff] font-bold hover:underline"
      >
         brands  
      </a>{" "}
      and agencies.
    </>,
    },
    {
      title: "Video Creators",
      desc: <>
      Organize raw footage, edited videos, thumbnails, and{" "}
      <a
        href="/cloud-storage-for-video-creators"
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#0072ff] font-bold hover:underline"
      >
         video creators  
      </a>{" "}
      production files while collaborating with editors, sponsors, and creative teams.
    </>,
    },
    {
      title: "Photographers",
      desc: <>
      Store RAW images, edited galleries, and event shoots in an organized space that helps{" "}
      <a
        href="/cloud-storage-for-photographers"
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#0072ff] font-bold hover:underline"
      >
         photographers   
      </a>{" "}
      simplify delivery and access.
    </>,
    },
    {
      title: "YouTubers ",
      desc: <>
      Store raw footage, edited videos, and project files in one organized space, making it easier for{" "}
      <a
        href="/cloud-storage-for-youtubers"
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#0072ff] font-bold hover:underline"
      >
        YouTubers   
      </a>{" "}
      to access, manage, and share content.
    </>,
    },
    {
      title: "Podcasters ",
      desc: "Manage audio recordings, episode drafts, and cover art while helping podcasters keep promotional assets accessible and ready to share.",
    },
    {
      title: "Designers & Artists",
      desc: <>
      Keep design files, creative assets, and{" "}
      <a
        href="/cloud-storage-for-designers"
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#0072ff] font-bold hover:underline"
      >
         designer   
      </a>{" "}
      client revisions organized throughout every stage of your projects.
    </>,
    },
    {
      title: "Bloggers & Writers",
      desc:"Store drafts, Bloggers research documents, images, and multimedia content in one place to streamline content creation and publishing.",
    },

  ];

  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqData = [
      {
        question: " What is SpaceByte and how does it help content creators?",
        answer:
          "SpaceByte is a cloud storage service for content creators. You can safely store your videos, photos, podcasts, and design files, access them from any device, and share them easily with your team or clients.",
      },
      {
        question: " Why do content creators need cloud backup storage?",
        answer:
          "Content creators often work with valuable files that take hours or even days to produce. Hardware failures, accidental deletion, or system crashes can result in permanent data loss. Cloud backup ensures that your important projects are safely stored online and can always be recovered when needed.",
      },
      {
        question: "What type of content can I store on SpaceByte?",
        answer:
          "You can store digital content like 4K/HD videos, RAW photos, design files, audio recordings, podcasts, editing projects, and social media assets. SpaceByte supports large uploads, so your files remain in original high quality without compression.",
      },
      {
        question: "Can I access my files from different devices?",
        answer:
          "Absolutely. SpaceByte allows you to access your files from multiple devices including desktops, laptops, tablets, and smartphones. This gives creators the flexibility to work from anywhere.",
      },
    ];
  
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [visible, setVisible] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);

    const reviews = useMemo(
        () => [
          {
            name: "Neha Sharma",
            role: "Digital Marketer",
            review: "SpaceByte offers fast, secure, and scalable cloud storage. I can access my files from any device, and collaboration is smooth. The data protection and secure authentication system are excellent for business use.",
            avatar: "",
            rating: 5,
            accent: "#2563EB",
            light: "#EAF2FF",
          },
          {
            name: "Karan Malhotra",
            role: "Video Creator",
            review: "As a video creator, I handle large 4K files daily. SpaceByte’s secure cloud storage and fast upload speed make backups effortless. The end-to-end encryption gives me complete peace of mind. Highly recommended for content creators.",
            avatar: "",
            rating: 5,
            accent: "#7C3AED",
            light: "#F3E8FF",
          },
          {
            name: "Vikram Desai",
            role: "Graphic Designer",
            review: "I use SpaceByte to store RAW images and client projects. The encrypted cloud backup and easy file sharing features save me so much time. It’s a reliable and best cloud storage solution for freelancers.",
            avatar: "",
            rating: 4,
            accent: "#0284C7",
            light: "#E0F2FE",
          },
          {
            name: "Priya Patel",
            role: "Social Media Manager",
            review: "As a social media manager, I store campaign creatives and videos on SpaceByte. Fast uploads and easy file sharing make content management simple and efficient.",
            avatar: "",
            rating:4,
            accent: "#2563EB",
            light: "#EAF2FF",
          },
        ],
        []
      );
    
      const total = reviews.length;
    
      useEffect(() => {
        const obs = new IntersectionObserver(
          ([e]) => {
            if (e.isIntersecting) setVisible(true);
          },
          { threshold: 0.12 }
        );
    
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
      }, []);
    
      const prev = () => setActiveIndex((p) => (p === 0 ? total - 1 : p - 1));
      const next = () => setActiveIndex((p) => (p === total - 1 ? 0 : p + 1));
  return (
    <Fragment>

      <Helmet>
        <title>Cloud Storage for Content Creators | Secure Storage</title>
        <meta
          name="description"
          content="Store, share, and collaborate on videos, photos, audio, and documents with SpaceByte. A secure and Cloud Storage for Content Creators with powerful features."
        />
        <meta name="keywords" content="Cloud Storage for Content Creators" />
        <link rel="canonical" href=" https://spacebyte.in/cloud-storage-for-content-creators" />
      </Helmet>

      <LandingNavbar
        color="transparent"
        className="fixed top-0 z-50 w-full flex-shrink-0 border-none bg-light-primary  shadow-lg"
        menuPosition="homepage-navbar"
      />
      
      <section className=" relative overflow-hidden !bg-gradient-to-r !from-[#0072ff]/8 !to-[#00c6ff]/8 py-24 md:py-32">
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#1E3A8A" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        <div
          className={`landing-container z-10 flex flex-col items-center justify-between pb-40 pt-100 lg:flex-row`}
        >
          <div className="mx-auto w-full max-w-6xl px-4 text-center">

            {/* Heading */}
            <h1 className="font-archivo text-[40px] md:text-[60px] font-bold leading-[1.2] tracking-[-0.02em] text-[#1f2937]">
              Smart Cloud Storage for   <br />
              <span className="color-primary-gradient"> Content Creators  </span>
              
              {/* <span className="color-primary-gradient"> SpaceByte</span> */}

            </h1>

            {/* Sub heading */}
            <p className="mx-auto mt-30 max-w-3xl font-inter text-[18px] md:text-[20px]  text-[#6b7280]">
              Cloud storage built for content creators to manage large files, collaborate faster,
               and protect your creative work anywhere.
            </p>

          <div className="my-36 pb-20 flex flex-col items-center justify-center gap-6 sm:flex-row">
            <a
              href="/register"
              className="inline-flex items-center justify-center 
              px-28 py-10 
              text-white font-bold
              bg-gradient-to-r from-[#0072ff] to-[#00c6ff]
              shadow-lg transition-all duration-300
              hover:from-[#0059d6] hover:to-[#00aaff]
              hover:shadow-2xl hover:-translate-y-1"
              style={{
              borderRadius: "12px"
            }}
            >
              Get 25 GB FREE
            </a>
            <a
              href="/pricing"
              className="inline-flex items-center justify-center
              px-36 py-8 font-bold
              text-[#0072ff]
              relative transition-all duration-300
              hover:-translate-y-1 hover:shadow-lg"
              style={{
                borderRadius: "12px",
                background: "linear-gradient(#e5f5fb, #e5f5fb) padding-box, linear-gradient(90deg, #0072ff, #00c6ff) border-box",
                border: "2px solid transparent"
              }}
              >
              View Pricing
            </a>
          </div>
          </div>
        </div>
      </section>  

      <section className="py-24 md:py-32 bg-gradient-to-br from-[#F8FAFC] to-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5 landing-container z-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-[#3B82F6] rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#06B6D4] rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 my-30 md:my-50 sm:px-6 lg:px-8 relative z-10">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-archivo font-bold text-[#111827] mb-10">
              Start Your Cloud Storage in <span className="color-primary-gradient">Three Steps</span>{" "}

            </h2>
            <p className="text-lg text-[#6B7280] max-w-2xl mx-auto pb-10 font-archivo">
              Start storing and sharing your files securely in minutes
            </p>
          </motion.div>

          {/* Steps */}
          <div className="grid md:grid-cols-3 gap-8 md:gap-12 relative landing-container">
            {/* Connecting Line */}
            <div className="hidden md:block absolute top-90 left-0 right-0 h-2 bg-gradient-to-r from-[#3B82F6] via-[#60A5FA] to-[#06B6D4] opacity-30"></div>

            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="relative"
              >
                <div className="flex flex-col items-center text-center">
                  {/* Icon Circle */}
                  <div className="relative mb-6">
                    <div className="w-80 h-80 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-full flex items-center justify-center relative z-10 shadow-lg">
                      <step.icon className="w-40 h-40 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-10  w-40 h-40 bg-white rounded-full flex items-center justify-center border-2 border-[#3B82F6] z-20">
                      <span className="text-[#1E3A8A] font-bold text-sm">{step.number}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-semibold font-archivo text-[#111827] my-20">
                    {step.title}
                  </h3>
                  <p className="text-[#6B7280] font-archivo max-w-xs">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Animated Line */}
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.5, delay: 0.5 }}
            className=" hidden md:block absolute top-[260px] left-[25%] right-[25%] h-3 bg-gradient-to-r from-[#3B82F6] to-[#06B6D4] rounded-full origin-left"
          ></motion.div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#f5f8ff] py-40 text-black lg:py-50">
        <div className="landing-container z-10 flex flex-col items-center justify-between gap-8 px-4 md:px-8 lg:flex-row">
          {/* Left Section */}
          <div className="relative w-full lg:w-1/2 flex justify-center">
            <div className="relative w-full max-w-[520px]">

              {/* Background Offset Block */}
              <div className="absolute -bottom-4 -right-4 h-full w-full rounded-2xl bg-gradient-to-br from-blue-600 to-blue-400" />

              {/* Image */}
              <img
                src="/images/contentcreators/contentcreators.webp"
                alt="cloud storage for content-creators"
                className="relative rounded-2xl shadow-2xl w-full object-cover"
              />
            </div>
          </div>

          {/* Right Content */}
          <div className="w-full space-y-16 lg:w-1/2 pt-14 md:pt-0">
            <h2 className="animate-fade-down font-archivo text-3xl font-bold md:text-4xl lg:text-5xl">
              Why <span className="color-primary-gradient">Content Creators </span>Need Cloud Storage
            </h2>
            <p className="color-gray font-inter animate-fade-down">
              Content creators handle large files like videos, photos, podcasts, and design assets every day. Managing them needs more than basic storage. A reliable cloud platform keeps files secure, organized, and easy to access, helping creators manage projects and focus on creativity.
            </p>

          </div>
        </div>

        {/* Elements */}
        <div className="absolute -bottom-40 left-0 hidden lg:block">
          <img
            src="/images/contentcreators/background.png"
            className="h-auto w-320"
            alt="shape"
          />
        </div>

      </section>

      <section
        id="Pricing"
        className="pricing-section relative overflow-hidden bg-white py-40 text-black lg:py-50"
      >
        <div className="landing-container z-10">
          <div className="mx-auto max-w-xl space-y-16 text-center">
            <h2 className="animate-fade-down font-archivo text-3xl font-bold md:text-4xl lg:text-5xl">
              Affordable <span className="color-primary-gradient"> Cloud Storage Pricing </span> Plans
            </h2>
            <p className="color-gray font-inter animate-fade-down">
              Get affordable pricing with no hidden fees, encrypted cloud storage,
              and fast, seamless access to your files anytime, anywhere.
            </p>
          </div>

          <div className="my-40 flex flex-col md:flex-row justify-center items-center gap-16">
            <BillingCycleRadio
              products={query.data?.products}
              selectedCycle={selectedCycle}
              onChange={setSelectedCycle}
              className="input-radio animate-fade-up flex justify-center items-center flex-wrap"
              size="lg"
            />
          </div>

          <PricingTable
            selectedCycle={selectedCycle}
            productLoader="pricingPage"
            className="animate-fade-down"
          />

          {/* Premium Section */}
          <div
            className="premium-bg animate-fade-down bg-light-primary relative mt-40 flex flex-col items-center justify-between space-y-8 overflow-hidden rounded-2xl border-primary p-16 shadow-lg md:flex-row md:space-y-0 md:p-32"
            style={{
              backgroundImage:
                "url('/images/LandingImg/ctamask.png'), url('/images/LandingImg/ctashape.png')",
              backgroundSize: 'contain, contain',
              backgroundRepeat: 'no-repeat, no-repeat',
              backgroundPosition: 'left center, right center',
            }}
          >
            {/* Premium Tag */}
            <div className="font-archivo absolute -left-54 top-16 w-160 rotate-[-45deg] transform rounded bg-warning text-center text-sm font-medium text-black md:-left-56 lg:-left-50 lg:top-20">
              Premium
            </div>

            {/* Icon and Text */}
            <div className="flex flex-col items-center gap-8 space-y-8 md:flex-row md:space-x-8 md:space-y-0">
              <img
                src="/images/LandingImg/cta-icon.webp"
                className="color-primary rounded-lg bg-white p-16"
              />
              <div className="text-center md:text-left">
                <h2 className="font-archivo text-3xl font-bold">
                  5 TB or More?
                </h2>
                <span className="color-primary font-archivo text-2xl font-semibold">
                  ₹0.15/GB{' '}
                  <span className="text-lg font-normal text-black">
                    /Monthly
                  </span>
                </span>
                <p className="font-inter mt-8 flex items-start justify-center gap-4 text-sm md:justify-start">
                  <CheckCircleOutlineIcon size="sm" /> 100% Safe & Secure with
                  End-to-End Encryption
                </p>
                <p className="font-inter mt-1 flex items-start justify-center gap-4 text-sm md:justify-start">
                  <CheckCircleOutlineIcon size="sm" /> Calculate the cost or
                  Reach out to us
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-row flex-wrap gap-8 sm:flex-col lg:flex-row justify-center">
              <div className="flex justify-center">
                {CalculateDialogTrigger()}
              </div>

              <div className="mt-0 flex justify-center">
                <a href="/contact" className="btn bg-[#f9fafb] border-2 border-[#0072ff] text-black hover:bg-[#0072ff] hover:text-white">
                  <span>Contact Us</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#f5f8ff]  py-40 md:pt-60 pb-40">
        <div className="landing-container z-10 mx-auto px-4">

          {/* Heading */}
          <div className="text-center mb-14">
              <h2 className="animate-fade-down font-archivo text-3xl font-bold md:text-4xl lg:text-5xl">
              Why SpaceByte is the <span className="color-primary-gradient">Best Choice for </span>
              <br />
                <span className="color-primary-gradient"> Content Creators</span>
              
              </h2>
              <p className="color-gray font-inter animate-fade-down py-16 md:py-24">
                SpaceByte offers content creators 
                <span className="color-primary-gradient font-bold"> secure, fast, and scalable cloud storage</span>.
                Easily store, manage,  and share large videos, high-resolution images, and audio files
                  while collaborating seamlessly and focusing on your creative workflow.
              </p>
          </div>

        <Swiper
        modules={[Pagination]}
        spaceBetween={24}
        pagination={{
          clickable: true,
          el: ".custom-pagination",
        }}
        breakpoints={{
          0: { slidesPerView: 1 },
          768: { slidesPerView: 2 },
          1024: { slidesPerView: 3 },
        }}
        style={{ paddingBottom: "30px" }}
      >
        {cards.map((card, index) => (
          <SwiperSlide key={index} style={{ display: "flex", height: "auto" }}>
            
            <div
              className="group bg-white rounded-2xl 
                        p-14 md:p-16 lg:p-18
                        border border-gray-200
                        hover:border-[#3B82F6]/40 hover:shadow-xl
                        transition-all duration-300
                        flex flex-col flex-1 min-h-[250px]"
            >
              {/* ICON */}
              <div
                className={`w-40 h-40 md:w-50 md:h-50 
                            bg-gradient-to-br ${card.color}
                            rounded-xl flex items-center justify-center 
                            mb-12 transition-transform duration-300 
                            group-hover:scale-110`}
              >
                {card.icon}
              </div>

              {/* TITLE */}
              <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mb-8">
                {card.title}
              </h3>

              {/* DESC */}
              <p className="color-gray font-base text-sm  leading-relaxed flex-grow">
                {card.desc}
              </p>
            </div>

          </SwiperSlide>
        ))}

        {/* 👇 Custom Pagination Below Cards */}
        <div
          className="custom-pagination"
          style={{
            marginTop: "40px",
            position: "relative",
            textAlign: "center",
          }}
        />
      </Swiper>
        </div>
      </section>

      <section id="security" className="py-30 md:py-40 bg-gradient-to-br from-white to-[#F8FAFC] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 landing-container z-10">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            {/* <span className="color-primary-gradient font-semibold text-3xl sm:text-4xl lg:text-5xl uppercase tracking-wide mb-16 block">
              Security
            </span> */}
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#111827] mb-12 md:mb-16">
                Easy <span className="color-primary-gradient ">Collaborate </span> { " " } 
               Without Confusion 
            </h2>
            <p className="text-lg text-[#6B7280] max-w-2xl mx-auto">
              Creating content isn’t a solo job. You work with editors, designers, clients, and marketing teams who need the right access at the right time. SpaceByte makes file sharing simple and stress-free so projects move faster.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left - Illustration */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative">
                {/* Main Shield */}
                <div className="w-full max-w-md mx-auto aspect-square relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-3xl opacity-10 blur-3xl"></div>
                  <div className="relative w-full h-full flex items-center justify-center">
                    <div className="w-200 h-200 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-full flex items-center justify-center shadow-2xl">
                      <Shield className="w-100 h-100 text-white" />
                    </div>
                  </div>

                  {/* Orbiting Icons */}
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                    className="absolute inset-0"
                  >
                    {[Lock, Database, FileCheck, Users, Eye].map((Icon, index) => {
                      const angle = (index * 360) / 5;
                      const radius = 140;
                      const x = Math.cos((angle * Math.PI) / 180) * radius;
                      const y = Math.sin((angle * Math.PI) / 180) * radius;

                      return (
                        <motion.div
                          key={index}
                          animate={{ rotate: -360 }}
                          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                          className="absolute top-1/2 left-1/2 w-40 h-40 bg-white rounded-xl shadow-lg flex items-center justify-center"
                          style={{
                            transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                          }}
                        >
                          <Icon className="w-20 h-20 text-[#3B82F6]" />
                        </motion.div>
                      );
                    })}
                  </motion.div>
                </div>
              </div>
            </motion.div>

            {/* Right - Features List */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              {securityFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ x: 10 }}
                  className="flex items-start gap-6  group"
                >
                  <div className="w-50 h-50 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <feature.icon className="w-11 h-11 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#111827] mb-3 ml-16">
                      {feature.title}
                    </h3>
                    <p className="text-[#6B7280] pb-10 ml-16">{feature.description}</p>
                  </div>
                </motion.div>
              ))}

              {/* Additional Security Badge */}
              {/* <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mt-20  p-20 bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] rounded-2xl"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Shield className="w-11 h-11 text-white" />
                  <h4 className="text-white font-semibold pl-5">SOC 2 Type II Certified</h4>
                </div>
                <p className="text-white/80 text-sm pt-6">
                  Independently verified security controls and compliance standards
                </p>
              </motion.div> */}
            </motion.div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-10 py-40 bg-white relative overflow-hidden">
        <div className="landing-container min-h-[280px] md:min-h-[380px] flex items-center justify-center relative rounded-2xl overflow-hidden">

          {/* Video Background */}
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src="/images/LandingImg/video.mp4"
            autoPlay
            loop
            muted
            playsInline
          />

          {/* Dark Overlay */}
          <div className="absolute inset-0 bg-black/60" />

          {/* Gradient Light */}
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 via-transparent to-blue-600/20" />

          {/* Content */}
          <div className="relative z-10 text-center max-w-4xl px-4">
            <h2 className="text-white text-3xl md:text-5xl font-semibold">
              Your Data, Your Control, Your Cloud
            </h2>

            <p className="text-white/80 text-base md:text-lg mt-20">
              A smarter cloud storage platform that gives you complete control over your data, file sharing, and digital privacy.

            </p>

            <div className="mt-20">
              <a href="/contact" className="btn bg-white text-black px-20 py-10 rounded-xl font-medium hover:scale-105 transition">
                <span>Contact Us</span>
              </a>
            </div>
          </div>

        </div>
      </section>

      <section className="w-full bg-white py-30 md:py-50 px-6 md:px-16 relative overflow-hidden">
      <div className="landing-container z-10 max-w-7xl  grid lg:grid-cols-2 gap-12 items-center">
        
        {/* LEFT SIDE */}
        <div>
          <h2 className="animate-fade-down font-archivo text-3xl font-bold md:text-4xl lg:text-5xl">
              Built for the Way 
              <br />
              <span className="color-primary-gradient"> Creators Work</span>
          </h2>

          <p className="color-gray font-inter animate-fade-down py-16 md:py-24 pr-10 md:pr-20">
            Creators across platforms manage large files and many projects. Organized storage and smooth file sharing help keep projects and creativity flowing.
          </p>

        </div>

        {/* RIGHT SIDE AUTO SCROLL */}
        <div className="relative h-[400px] overflow-hidden">
          <div className="animate-scroll space-y-16 ">
            {[...services, ...services].map((service, index) => (
              <div
                key={index}
                className="bg-gray-50 p-14 rounded-xl shadow-md group bg-[#f5f8ff] rounded-2xl 
                        p-18 md:p-18 lg:p-18
                        border border-gray-200
                        hover:border-[#3B82F6]/40 hover:shadow-xl
                        transition-all duration-300
                        flex flex-col flex-1 min-h-[150px] "
              >
                <h3 className="text-xl font-semibold text-gray-900">
                  {service.title}
                </h3>
                <p className="mt-3 text-[#6B7280]">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Animation CSS */}
      <style>
        {`
          @keyframes scroll {
            0% { transform: translateY(0); }
            100% { transform: translateY(-50%); }
          }

          .animate-scroll {
            animation: scroll 30s linear infinite;
          }

          .animate-scroll:hover {
            animation-play-state: paused;
          }
        `}
      </style>
      </section>
      
      <section id="faq" className="bg-[#f5f8ff] relative overflow-hidden py-24 text-black lg:py-32">
        <div className="landing-container z-10">
          <div className="section-wrapper">
            <div className="section-inner">
              {/* LEFT PANEL */}
              <div className="left-panel">
                <div className="badge font-archivo">
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <circle cx="5" cy="5" r="5" fill="#38bdf8" />
                  </svg>
                  SpaceByte Cloud
                </div>

                <h2 className="left-title font-archivo">
                  Best Cloud Storage <br />
                  Secure, Fast and  <br />
                  Reliable
                </h2>

                <p className="left-sub font-archivo">
                  Everything you need to store, share, and access your files anytime, anywhere.
                </p>

                <div className="illustration">
                  <div className="illus-card">
                    <div className="illus-row">
                      <div className="illus-icon ic-blue">☁️</div>
                      <div className="illus-text font-archivo">
                        <strong>My Storage</strong>
                        <span>18.3 GB used of 25 GB</span>
                        <div className="illus-bar">
                          <div className="illus-bar-fill" style={{ width: "73%" }} />
                        </div>
                      </div>
                    </div>

                    <div className="illus-row">
                      <div className="illus-icon ic-sky">📁</div>
                      <div className="illus-text font-archivo">
                        <strong>Shared Folders</strong>
                        <span>12 folders · 3 collaborators</span>
                      </div>
                    </div>

                    <div className="illus-row">
                      <div className="illus-icon ic-white">🔒</div>
                      <div className="illus-text">
                        <strong>End-to-End Encrypted</strong>
                        <span>All files secured in transit</span>
                      </div>
                    </div>

                    <div className="stats-row">
                      <div className="stat-chip">
                        <strong>25 GB</strong>
                        <span>Free Storage</span>
                      </div>
                      <div className="stat-chip">
                        <strong>99.9%</strong>
                        <span>Uptime</span>
                      </div>
                      <div className="stat-chip">
                        <strong>24/7</strong>
                        <span>Support</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT PANEL */}
              <div className="right-panel">
                <p className="section-tag">Help Center</p>

                <h2 className="section-title">
                  Quick Reference <br />
                  Guide
                </h2>

                <p className="section-desc">
                  Find answers to the most common questions about SpaceByte Cloud Storage.
                </p>

                <div className="accordion">
                  {faqData.map((item, index) => (
                    <div
                      key={index}
                      className={`acc-item ${openIndex === index ? "open" : ""}`}
                    >
                      <button className="acc-header" onClick={() => toggle(index)}>
                        <div className="acc-num">
                          {String(index + 1).padStart(2, "0")}
                        </div>

                        <span className="acc-label">{item.question}</span>

                        <div className="acc-chevron">
                          <svg viewBox="0 0 24 24" fill="none">
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </div>
                      </button>

                      <div className="acc-body">
                        <div
                          className="acc-body-inner"
                          dangerouslySetInnerHTML={{ __html: item.answer }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        ref={ref}
        className="relative overflow-hidden bg-white py-30 md:py-50"
      >
        {/* Background Orbs (same as ::before/::after) */}
        <div className="pointer-events-none absolute -left-[180px] -top-[180px] h-[560px] w-[560px] rounded-full bg-[radial-gradient(circle,#dbeafe40,transparent_70%)]" />
        <div className="pointer-events-none absolute -bottom-[160px] -right-[160px] h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle,#ede9fe40,transparent_70%)]" />

        <div className="relative z-[1] mx-auto grid max-w-[1280px] grid-cols-2 items-stretch gap-[56px] px-[clamp(20px,5vw,72px)] max-[900px]:grid-cols-1 max-[900px]:gap-[40px]">
          {/* ───────────────── LEFT ───────────────── */}
          <div
            className={[
              "transition-all duration-700 ease-out",
              visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-7",
            ].join(" ")}
          >


            {/* Heading */}
            <h2 className="my-10 pt-6 md:pt-30 font-archivo text-[clamp(26px,3.2vw,46px)] font-extrabold leading-[1.15] tracking-[-0.025em] text-[#0f172a]">
              What Our {" "}
              <span className="bg-gradient-to-br font-archivo color-primary-gradient">
                Customers Say
              </span>{" "}

            </h2>

            {/* Text */}
            <p className="mb-10  max-w-[440px] font-archivo text-[clamp(14px,1.1vw,16px)] leading-[1.82] text-[#64748b]">
              See how SpaceByte delivers secure file storage, high-speed uploads,
              and advanced privacy protection. Read verified reviews before making your cloud storage decision.
            </p>



            {/* Controls */}
            <div className="flex flex-wrap my-20  items-center gap-3">
              <button
                onClick={prev}
                className="flex h-[50px] w-[50px] items-center justify-center rounded-full border-[1.5px] border-[#e0e7ff] bg-white text-[#2563eb] shadow-[0_2px_12px_rgba(37,99,235,0.09)] transition-all duration-200 ease-out hover:scale-[1.08] hover:border-[#2563eb] hover:bg-[#2563eb] hover:text-white hover:shadow-[0_8px_24px_rgba(37,99,235,0.28)]"
              >
                <ArrowLeftIcon />
              </button>

              <button
                onClick={next}
                className="flex h-[50px] w-[50px] items-center justify-center rounded-full border-[1.5px] border-[#e0e7ff] bg-white text-[#2563eb] shadow-[0_2px_12px_rgba(37,99,235,0.09)] transition-all duration-200 ease-out hover:scale-[1.08] hover:border-[#2563eb] hover:bg-[#2563eb] hover:text-white hover:shadow-[0_8px_24px_rgba(37,99,235,0.28)]"
              >
                <ArrowRightIcon />
              </button>
            </div>

            {/* Dots */}
            <div className="ml-[6px] flex gap-[6px] my-16">
              {reviews.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  aria-label={`Slide ${i + 1}`}
                  className={[
                    "h-[8px] rounded-[4px] transition-all duration-300",
                    i === activeIndex ? "w-[26px] bg-[#2563eb]" : "w-[8px] bg-[#cbd5e1]",
                  ].join(" ")}
                />
              ))}
            </div>

            {/* Progress */}
            <div className="mt-5 h-[3px] max-w-[220px] overflow-hidden rounded bg-[#e2e8f0]">
              <div
                className="h-full rounded bg-gradient-to-r from-[#2563eb] to-[#7c3aed] transition-all duration-500"
                style={{ width: `${((activeIndex + 1) / total) * 100}%` }}
              />
            </div>
          </div>

          {/* ───────────────── RIGHT (SLIDER) ───────────────── */}
        <div
          className={[
            "w-full transition-all duration-700 ease-out delay-200",
            visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-7",
          ].join(" ")}
        >
        <div className="relative w-full overflow-hidden">
          <div
            className="flex transition-transform duration-700 ease-in-out"
            style={{
              transform: `translateX(-${activeIndex * 100}%)`,
            }}
          >
            {reviews.map((item, i) => (
              <div key={i} className="w-full flex-shrink-0 px-3">
                <ReviewCard item={item} isActive={i === activeIndex} />
              </div>
            ))}
          </div>
        </div>
        </div>
      </div>
      </section>

      <section className="py-20 md:py-32 bg-gradient-to-br from-[#1E3A8A] via-[#3B82F6] to-[#60A5FA] relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 landing-container z-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="cta-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#cta-grid)" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-40 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            {/* Icon */}
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: 'spring', delay: 0.2 }}
              className="inline-flex items-center justify-center w-60 h-60 bg-white/20 backdrop-blur-sm rounded-2xl mb-12"
            >
              <Sparkles className="w-30 h-30 text-white" />
            </motion.div>

            {/* Heading */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white"
            >
              Ready to Secure Your Data?
            </motion.h2>

            {/* Subheading */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="text-lg sm:text-xl text-white/90 my-16 max-w-2xl mx-auto"
            >
              Join thousands of businesses trusting secure cloud storage to protect critical data.

            </motion.p>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-10 justify-center items-center"
            >
              <a href="/register" className="group bg-white text-[#1E3A8A] px-16 py-14 rounded-xl font-semibold hover:bg-gray-100 transition-all hover:shadow-2xl hover:shadow-white/20 transform hover:-translate-y-1 flex items-center gap-2 btn">
                Get 25 GB FREE - No Card Needed
              </a>

            </motion.div>

            {/* Trust Badges */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="mt-20 flex flex-wrap justify-center items-center gap-8 text-white/80 text-sm"
            >

            </motion.div>
          </motion.div>
        </div>
      </section>

      <footer className="Footer-section relative overflow-hidden bg-[#0F172A] text-white pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main Footer Content */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 mb-12">
            {/* Brand Column */}
            <div className="col-span-2 md:col-span-4 lg:col-span-2">
              <div className="space-y-4 mr-12 padding-logo padding-top">
                <a href="https://spacebyte.in/" className="flex-shrink-0">
                  <img
                    src="/images/logo-light.png"
                    alt="Logo"
                    className="site-logo"
                  />
                </a>
                <div>
                  <p className="font-medium pt-8 pb-10">SPACEBYTE CLOUD STORAGE</p>
                  <p className="pb-10 pt-3 flex items-center gap-2">
                    311, Maruti Plaza, Sardar Patel Chowk, Krishnanagar, Ahmedabad, Gujarat,
                    India - 382345</p>
                </div>
                <div>
                  <p className="font-bold text-base pb-10 pt-3">FOLLOW US FOR "SPACEBYTE.IN"</p>
                  <div className="flex gap-4 mt-3 logg mb-10">
                    <ul className="flex space-x-16 pt-3 pr-10">
                      <li className="bg-light rounded-lg p-8">
                        <a href="https://www.facebook.com/profile.php?id=61567307277313&mibextid=ZbWKwL">
                          <FacebookIcon className="text-white" />
                        </a>
                      </li>
                      <li className="bg-light rounded-lg p-8">
                        <a href="https://www.instagram.com/spacebyte.in?igsh=MmZidnprMXZtbTho ">
                          <InstagramIcon className="text-white" />
                        </a>
                      </li>
                      <li className="bg-light rounded-lg p-8">
                        <a href="https://youtube.com/@spacebytecloudstorage?si=lt2mfOM7kSaN3g6B">
                          <YoutubeIcon className="text-white" />
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Column */}
            <div className='py-8 md:py-20 px-30'>
              <h2 className="font-semibold text-xl mb-10  text-start ">Product</h2>
              <ul className="space-y-3 text-sm text-gray-400 text-start">
                <li>
                  <a href="#features" className="hover:text-white transition-colors text-start">
                    Features
                  </a>
                </li>
                <li>
                  <a href="/Pricing" className="hover:text-white transition-colors">
                    Pricing
                  </a>
                </li>
                <li>
                  <a href="#security" className="hover:text-white transition-colors">
                    Security
                  </a>
                </li>
                <li>
                </li>
              </ul>
            </div>

            {/* Company Column */}
            <div className='py-8 md:py-20 px-30'>
              <h2 className="font-semibold text-xl mb-10  text-start">Company</h2>
              <ul className="space-y-3 text-sm text-gray-400 text-start">
                <li>
                  <a href="/about-us" className="hover:text-white transition-colors">
                    About Us
                  </a>
                </li>
                <li>
                  <a href="/pages/articles" className="hover:text-white transition-colors">
                    Articles
                  </a>
                </li>
                <li>
                  <a href="/contact" className="hover:text-white transition-colors">
                    Contact Us
                  </a>
                </li>
              </ul>
            </div>

            {/* Resources Column */}
            <div className='py-8 md:py-20 px-30'>
              <h2 className="font-semibold text-xl mb-10  text-start">Resources</h2>
              <ul className="space-y-3 text-sm text-gray-400 text-start">
                <li>
                  <a href="#faq" className="hover:text-white transition-colors">
                    FAQs
                  </a>
                </li>
                <li>
                  <a href="/contact" className="hover:text-white transition-colors">
                    Help Center
                  </a>
                </li>
                {/* <li>
                <a href="https://spacebyte.in/api-docs" className="hover:text-white transition-colors">
                  Developers
                </a>
              </li> */}
              </ul>
            </div>

            {/* Legal Column */}
            <div className='py-8 md:py-20 px-30'>
              <h3 className="font-semibold text-xl mb-10  text-start">Legal</h3>
              <ul className="space-y-3 text-sm text-gray-400 text-start">
                <li>
                  <a href="/pages/privacy-policy" className="hover:text-white transition-colors">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="/pages/terms-of-service" className="hover:text-white transition-colors">
                    Terms of Service
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="font-inter border-t border-t-primary/80 py-16 text-center mt-5">
            <p className="text-sm">
              Copyright © {new Date().getFullYear()}{' '}
              <a href="/login" className="color-primary-gradient">
                SpaceByte Cloud Storage
              </a>
              , All Rights Reserved
            </p>
          </div>
        </div>
      </footer>
    </Fragment>
  );
}

function CalculateDialogTrigger() {
  const form = useForm<ContactPagePayload>({
    defaultValues: {
      message: '', // Initialize message field
    },
  });
  const [isOpen, setIsOpen] = useState(false);
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const submitForm = useSubmitContactForm(form);
  const { verify, isVerifying } = useRecaptcha('contact');

  return (
    <>
      <DialogTrigger type="modal" isOpen={isOpen} onOpenChange={setIsOpen}>
        <button className="btn w-full rounded-lg !text-white font-semibold
                        !bg-gradient-to-r !from-[#0072ff] !to-[#00c6ff]  shadow-lg
                        hover:shadow-xl transition">
          <span>Get Quote</span>
        </button>
        <Dialog className="w-full max-w-lg rounded-xl p-6">
          <DialogHeader
            className="font-archivo text-2xl font-semibold"
            showDivider
          >
            <Trans message="Storage Cost Calculator" />
          </DialogHeader>
          <DialogBody>
            <div className="font-inter flex items-center justify-center">
              Enter the storage size (whole number greater than **5 TB**) to
              calculate your monthly cost.
            </div>
            <Form
              form={form}
              className="mt-4 flex flex-col items-center text-center"
              onSubmit={async value => {
                calculateCost(value);
              }}
            >
              <FormTextField
                label={<Trans message="Enter Storage in TB" />}
                name="tb"
                required
                type="number"
                className="mb-6 w-full rounded-lg p-2"
              />
              <div className="text-red-500 text-md" id="errorMsg"></div>
              <div className="text-blue-500 text-md" id="resultOutput"></div>
              <div className="mt-6 flex w-full justify-between gap-4">
                <button className="btn-warning mt-16" type="submit">
                  Calculate
                </button>
                <Button
                  color="positive"
                  variant="outline"
                  className="mt-16 rounded-lg"
                  type="button"
                  onClick={() => {
                    const resultOutput =
                      document.getElementById('resultOutput')?.textContent;
                    form.setValue('message', resultOutput || ''); // Set the value of message field
                    setIsOpen(false); // Close current dialog
                    setIsContactDialogOpen(true); // Open Contact Us dialog
                  }}
                >
                  Contact Us
                </Button>
              </div>
            </Form>
          </DialogBody>
        </Dialog>
      </DialogTrigger>

      {isContactDialogOpen && (
        <DialogTrigger
          type="modal"
          isOpen={isContactDialogOpen}
          onOpenChange={setIsContactDialogOpen}
        >
          <Dialog className="w-full max-w-lg rounded-xl p-6">
            <DialogHeader
              className="font-archivo text-2xl font-semibold"
              showDivider
            >
              <Trans message="Contact Us" />
            </DialogHeader>
            <DialogBody>
              {submitForm.isPending ? (
                <FullPageLoader />
              ) : (
                <Form
                  form={form}
                  className="flex flex-col gap-4"
                  onSubmit={async value => {
                    const isValid = await verify();
                    if (isValid) {
                      submitForm.mutate(value, {
                        onSuccess: () => {
                          setIsContactDialogOpen(false); // Close the dialog on success
                        },
                      });
                    }
                  }}
                >
                  <FormTextField
                    label={<Trans message="Name" />}
                    name="name"
                    required
                    className="rounded-lg p-2"
                  />
                  <FormTextField
                    label={<Trans message="Email" />}
                    name="email"
                    required
                    type="email"
                    className="rounded-lg p-2"
                  />
                  <FormTextField
                    label={<Trans message="Mobile Number" />}
                    name="mobileNumber"
                    required
                    type="number"
                    className="rounded-lg p-2"
                  />
                  <FormTextField
                    label={<Trans message="Message" />}
                    name="message"
                    required
                    inputElementType="textarea"
                    className="rounded-lg p-2"
                    rows={5}
                  />

                  <div className="mt-4 flex w-full justify-between gap-4">
                    <Button
                      variant="flat"
                      onClick={() => setIsContactDialogOpen(false)}
                      className="rounded-lg px-4 py-2 text-lg"
                    >
                      <Trans message="Back" />
                    </Button>
                    <Button
                      type="submit"
                      variant="flat"
                      color="primary"
                      className="rounded-lg px-4 py-2 text-lg"
                    >
                      <Trans message="Send Message" />
                    </Button>
                  </div>
                </Form>
              )}
            </DialogBody>
          </Dialog>
        </DialogTrigger>
      )}
    </>
  );
}

function calculateCost({ tb }: any) {
  const tbInput: any = tb;
  const errorMsg: any = document.getElementById('errorMsg');
  const resultOutput: any = document.getElementById('resultOutput');

  // Reset messages
  errorMsg.textContent = '';
  resultOutput.textContent = '';

  // Validation: Ensure input is a positive integer and greater than 5
  if (!/^\d+$/.test(tbInput)) {
    errorMsg.textContent = 'Error: Storage must be a whole number!';
    return;
  }

  const tbValue = parseInt(tbInput, 10);

  if (tbValue <= 5) {
    errorMsg.textContent = 'Error: Storage must be greater than 5 TB!';
    return;
  }

  // Conversion and cost calculation
  const gbStorage = tbValue * 1024; // Convert TB to GB
  const cost = gbStorage * 0.15; // Calculate cost

  // Display result
  resultOutput.innerHTML = `
      Storage: <strong>${tbValue.toLocaleString()} TB</strong><br>
      Total Cost: <strong>₹${cost.toFixed(2)}/month</strong><br>
      GST and Convenience Fee Extra**
  `;
}

function ReviewCard({
  item,
  isActive,
}: {
  item: any;
  isActive: boolean;
}) {
  const { name, role, avatar, review, accent, light, rating } = item;
  
  // Extract the first letter of the name
  const initial = name?.charAt(0).toUpperCase() || "U";

  return (
    <div
      className={[
        "relative flex-shrink-0 overflow-hidden m-10 rounded-[28px] border-[1.5px] p-[clamp(24px,3.5vw,42px)] transition-all duration-500 flex flex-col justify-between",
        isActive ? "translate-y-[-4px] scale-[1.015]" : "scale-[0.97]",
      ].join(" ")}
      style={{
        minWidth: "clamp(270px, 42vw, 450px)",
        maxWidth: "clamp(270px, 42vw, 450px)",
        background: isActive
          ? `linear-gradient(150deg, ${light} 0%, white 100%)`
          : `linear-gradient(150deg, ${light}88 0%, #f8fafc 100%)`,
        borderColor: isActive ? `${accent}50` : "#e2e8f0",
        boxShadow: isActive
          ? `0 24px 60px ${accent}1a, 0 4px 16px ${accent}10`
          : "0 2px 12px rgba(0,0,0,0.05)",
        opacity: isActive ? 1 : 0.68,
      }}
    >
      <div>
        {/* Rating Section */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex gap-1">
            {[...Array(5)].map((_, i) => (
              <span key={i} style={{ color: i < rating ? accent : "#cbd5e1" }} className="text-[18px]">
                ★
              </span>
            ))}
          </div>
          {/* Subtle Quote Mark */}
          <span className="font-serif text-[60px] leading-none opacity-10 absolute right-8 top-6" style={{ color: accent }}>
            “
          </span>
        </div>

        <p className="font-archivo text-[clamp(14px,1.15vw,16px)] font-medium leading-[1.7] mb-8 text-[#334155]">
          "{review}"
        </p>
      </div>

      <div className="flex items-center gap-10">
        {/* AVATAR / INITIAL BOX */}
        <div 
          className="flex h-50 w-50 shrink-0 items-center justify-center rounded-full border-2 font-archivo text-[20px] font-bold"
          style={{ 
            borderColor: `${accent}40`,
            background: isActive ? `${accent}15` : "#f1f5f9",
            color: accent 
          }}
        >
          {avatar ? (
            <img src={avatar} alt={name} className="h-full w-full object-cover rounded-full" />
          ) : (
            <span>{initial}</span> 
          )}
        </div>

        <div className="overflow-hidden ">
          <h4 className="truncate font-archivo text-[17px] font-bold text-[#0f172a] leading-tight">
            {name}
          </h4>
          <p className="truncate text-[13px] font-semibold" style={{ color: accent }}>
            {role}
          </p>
        </div>
      </div>

      <div
        className="absolute bottom-0 left-0 h-[3px] rounded-bl-[28px] transition-all duration-[650ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
        style={{
          width: isActive ? "100%" : "0%",
          background: `linear-gradient(90deg, ${accent}, ${accent}44)`,
        }}
      />
    </div>
  );
}